const VERTICAL_SWAP_TARGETS = ['.product-card__images-wrapper', '.product-card__price', '.product-card__title-row'];

class ProductCardSiblings extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('.product-card, product-card, .horizontal-product-card, horizontal-product-card');
    this.handle = this.getAttribute('data-product-handle');
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const swatch = event.target.closest('[data-sibling-handle]');
    if (!swatch || !this.contains(swatch)) return;
    if (swatch.classList.contains('is-selected')) return;
    event.preventDefault();
    this.switchTo(swatch);
  };

  loadGroup() {
    if (!this.group) {
      const sectionId =
        this.card && this.card.hasAttribute('data-static-media')
          ? 'product-card-color-change-static'
          : 'product-card-color-change';
      this.group = fetch(`/products/${this.handle}?section_id=${sectionId}`)
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error('fetch failed'))))
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .catch((error) => {
          this.group = null;
          throw error;
        });
    }
    return this.group;
  }

  async switchTo(swatch) {
    const handle = swatch.getAttribute('data-sibling-handle');
    const url = swatch.getAttribute('data-sibling-url');
    const id = swatch.getAttribute('data-sibling-id');
    if (!handle || !this.card) return;

    this.querySelectorAll('[data-sibling-handle].is-selected').forEach((el) => el.classList.remove('is-selected'));
    swatch.classList.add('is-selected');

    if (url) {
      this.card
        .querySelectorAll(
          'a.product-card__link, a.product-card__title, a.horizontal-product-card__link, a.horizontal-product-card__title'
        )
        .forEach((a) => {
          a.href = url;
        });
    }

    this.updateWishlist(handle, id);

    const image = this.card.querySelector('.product-card__images-wrapper, .horizontal-product-card__image');
    if (image) image.style.opacity = '0.5';

    const doc = await this.loadGroup().catch(() => null);
    if (image) image.style.opacity = '';

    const source = doc && doc.querySelector(`template[data-sibling-content="${handle}"]`);
    if (!source) {
      window.location.href = url || `/products/${handle}`;
      return;
    }

    const fragment = source.content.cloneNode(true);

    if (this.card.querySelector('.horizontal-product-card__image')) {
      this.applyHorizontal(fragment);
      this.applyQuickAdd(fragment, 'horizontal-product-card__size-select');
    } else {
      VERTICAL_SWAP_TARGETS.forEach((selector) => {
        const next = fragment.querySelector(selector);
        const current = this.card.querySelector(selector);
        if (next && current) current.replaceWith(next);
      });
      this.applyQuickAdd(fragment);
    }
  }

  applyQuickAdd(fragment, extraClass) {
    const currentAdd = this.card.querySelector('.product-card__add');
    const nextAdd = fragment.querySelector('.product-card__add');
    if (!currentAdd || !nextAdd) return;
    currentAdd.replaceWith(nextAdd);
    this.applySizeSelect(fragment.querySelector('.product-card__size-select'), extraClass);
  }

  applySizeSelect(nextSizes, extraClass) {
    const currentSizes = this.card.querySelector('.product-card__size-select');
    const prevSizeTitle = currentSizes?.querySelector('.is-selected')?.textContent.trim();
    if (nextSizes && extraClass) nextSizes.classList.add(extraClass);
    if (currentSizes && nextSizes) {
      currentSizes.replaceWith(nextSizes);
    } else if (currentSizes) {
      currentSizes.remove();
    } else if (nextSizes) {
      const anchor = this.closest('.product-card__colors-wrapper') || this;
      anchor.before(nextSizes);
    }
    this.syncSelectedSize(prevSizeTitle);
  }

  syncSelectedSize(prevSizeTitle) {
    if (!prevSizeTitle) return;
    const row = this.card.querySelector('.product-card__size-select');
    if (!row) return;
    const buttons = [...row.querySelectorAll('[data-variant-id]')];
    const target = buttons.find((btn) => btn.textContent.trim() === prevSizeTitle && !btn.disabled);
    if (!target) return;
    buttons.forEach((btn) => {
      const isSelected = btn === target;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
    const variantInput = this.card.querySelector('.product-card__add input[name="id"]');
    if (variantInput) variantInput.value = target.dataset.variantId;
  }

  applyHorizontal(fragment) {
    const sourceImage = fragment.querySelector('.product-card__images-wrapper img') || fragment.querySelector('img');
    const targetImage = this.card.querySelector('.horizontal-product-card__image');
    if (sourceImage && targetImage) {
      const nextSrc = sourceImage.getAttribute('src');
      const nextSrcset = sourceImage.getAttribute('srcset');
      if (nextSrc) targetImage.setAttribute('src', nextSrc);
      if (nextSrcset) targetImage.setAttribute('srcset', nextSrcset);
      else targetImage.removeAttribute('srcset');
    }

    const nextPrice = fragment.querySelector('.product-card__price');
    const currentPrice = this.card.querySelector('.product-card__price');
    if (nextPrice && currentPrice) currentPrice.replaceWith(nextPrice);

    const nextTitle = fragment.querySelector('.product-card__title');
    const currentTitle = this.card.querySelector('.horizontal-product-card__title');
    if (nextTitle && currentTitle) currentTitle.textContent = nextTitle.textContent.trim();

    const nextRating = fragment.querySelector('.product-card__title-row .product-card__rating');
    const currentRating = this.card.querySelector('.horizontal-product-card__rating');
    if (nextRating && currentRating) {
      currentRating.innerHTML = nextRating.innerHTML;
      currentRating.classList.toggle(
        'product-card__rating--empty',
        nextRating.classList.contains('product-card__rating--empty')
      );
    }
  }

  updateWishlist(handle, id) {
    const wishlist = this.card.querySelector('.st-wishlist-button');
    if (wishlist) {
      const fresh = wishlist.cloneNode(true);
      fresh.setAttribute('data-handle', handle);
      if (id) fresh.setAttribute('data-product-id', id);
      fresh.classList.remove('st-initialized', 'st-is-added');
      wishlist.replaceWith(fresh);
    }

    const heart = this.card.querySelector('cart-wishlist[data-handle]');
    if (heart) {
      const freshHeart = heart.cloneNode(true);
      freshHeart.setAttribute('data-handle', handle);
      if (id) freshHeart.setAttribute('data-product-id', id);
      freshHeart.classList.remove('is-active');
      heart.replaceWith(freshHeart);
    }
  }
}

if (!customElements.get('product-card-siblings')) {
  customElements.define('product-card-siblings', ProductCardSiblings);
}
